import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Search, Filter, Download, Trash2, PackageX } from "lucide-react";
import { toast } from "sonner";

const MOTIVOS_BAJA = ["Obsolescencia tecnológica", "Daño irreparable", "Fin de vida útil",
  "Costo de reparación excede el valor del equipo", "Falla recurrente", "Siniestro", "Otro"];

export default function Bajas() {
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const [bajas, setBajas] = useState([]);
  const [stats, setStats] = useState({ total: 0, borradores: 0, completados: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ folio: "", cliente: "", estado: "", motivo: "", fecha_desde: "", fecha_hasta: "" });

  const buildParams = (f) => {
    const p = new URLSearchParams();
    if (f.folio) p.set("folio", f.folio);
    if (f.cliente) p.set("cliente", f.cliente);
    if (f.estado && f.estado !== "todos") p.set("estado", f.estado);
    if (f.motivo && f.motivo !== "todos") p.set("motivo", f.motivo);
    if (f.fecha_desde) p.set("fecha_desde", f.fecha_desde);
    if (f.fecha_hasta) p.set("fecha_hasta", f.fecha_hasta);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/api/bajas?${buildParams(filters)}`),
        api.get("/api/bajas/stats"),
      ]);
      setBajas(r.data);
      setStats(s.data);
    } catch { toast.error("Error al cargar informes de baja"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uf = (f, v) => setFilters((p) => ({ ...p, [f]: v }));
  const clearFilters = () => {
    setFilters({ folio: "", cliente: "", estado: "", motivo: "", fecha_desde: "", fecha_hasta: "" });
    setTimeout(load, 50);
  };

  const downloadPdf = async (b) => {
    try {
      const r = await api.get(`/api/bajas/${b.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${b.folio}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`¿Eliminar el informe de baja ${b.folio}?`)) return;
    try { await api.delete(`/api/bajas/${b.id}`); toast.success("Informe eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <PackageX className="h-5 w-5 text-[#0FB4B7] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#384091] truncate">Informes de Baja</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Filtros</span>
            </Button>
            <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white"
              onClick={() => navigate("/tecnico/bajas/nuevo")}>
              <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Nuevo</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total", value: stats.total, color: "#384091" },
            { label: "Borradores", value: stats.borradores, color: "#D97706" },
            { label: "Completados", value: stats.completados, color: "#059669" },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {showFilters && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Folio</Label><Input value={filters.folio} onChange={(e) => uf("folio", e.target.value)} className="h-11 sm:h-9 text-xs" placeholder="IB-..." autoComplete="off" /></div>
                <div><Label className="text-xs">Cliente</Label><Input value={filters.cliente} onChange={(e) => uf("cliente", e.target.value)} className="h-11 sm:h-9 text-xs" placeholder="Buscar..." autoComplete="off" /></div>
                <div><Label className="text-xs">Estado</Label>
                  <Select value={filters.estado} onValueChange={(v) => uf("estado", v)}>
                    <SelectTrigger className="h-11 sm:h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent><SelectItem value="todos">Todos</SelectItem><SelectItem value="borrador">Borrador</SelectItem><SelectItem value="completado">Completado</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Motivo</Label>
                  <Select value={filters.motivo} onValueChange={(v) => uf("motivo", v)}>
                    <SelectTrigger className="h-11 sm:h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {MOTIVOS_BAJA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Fecha desde</Label><Input type="date" value={filters.fecha_desde} onChange={(e) => uf("fecha_desde", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
                <div><Label className="text-xs">Fecha hasta</Label><Input type="date" value={filters.fecha_hasta} onChange={(e) => uf("fecha_hasta", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button size="sm" className="h-11 sm:h-9 bg-[#0FB4B7] text-white" onClick={load}><Search className="h-3.5 w-3.5 mr-1" /> Buscar</Button>
                <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={clearFilters}>Limpiar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-center text-gray-400 py-12">Cargando...</p>
        ) : bajas.length === 0 ? (
          <p className="text-center text-gray-400 py-12">Sin informes de baja</p>
        ) : (
          <div className="space-y-2">
            {bajas.map((b) => (
              <Card key={b.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <Link to={`/tecnico/bajas/${b.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <PackageX className="h-4 w-4 text-[#0FB4B7] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {b.folio} — {b.cliente_nombre}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {[b.marca, b.modelo].filter(Boolean).join(" ") || "Equipo"}
                        {b.motivo_baja ? ` · ${b.motivo_baja}` : ""} · {b.fecha} · {b.tecnico_nombre}
                      </p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1.5 flex-shrink-0 self-end sm:self-auto">
                    <Badge className={b.estado === "completado" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-amber-50 text-amber-700 border-0"}>
                      {b.estado === "completado" ? "Completado" : "Borrador"}
                    </Badge>
                    {b.estado === "completado" && (
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(b)} title="Descargar PDF">
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {(isAdmin || b.estado === "borrador") && (
                      <Button variant="ghost" size="sm" onClick={() => remove(b)} title="Eliminar"
                        className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
